import React, { useState, useContext } from "react";
import NextLink from "next/link";
import {
  Box,
  Button,
  Stack,
  Image,
  Flex,
  Link,
  PseudoBox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/core";
import useAuth from "context/Auth";
import { useRouter } from "next/router";

import UserCircleOutline from "svg/icons/user-circle.svg";
import MenuOutline from "svg/icons/menu.svg";
import XOutline from "svg/icons/x.svg";

import Basket from "components/home/Basket";
import CustomLink from "components/shared/CustomLink";

import { fuego } from "@nandorojo/swr-firestore";
import { ContextStore } from "context/ContextStore";

const NavLink = ({ name, href, ...rest }) => {
  return (
    <CustomLink
      href={href}
      borderBottom="2px"
      borderColor="transparent"
      _hover={{ borderColor: "brand.400", color: "gray.900" }}
      px="1"
      pt="1"
      display="inline-flex"
      h="full"
      alignItems="center"
      color="gray.500"
      transition="all 150ms ease-in-out"
      _focus={{ outline: "none", color: "gray.900" }}
      fontSize="md"
      lineHeight="base"
      fontWeight="medium"
      {...rest}
    >
      {name}
    </CustomLink>
  );
};

const MobileNavLink = ({ name, href, ...rest }) => {
  return (
    <CustomLink
      href={href}
      display="block"
      pl={{ base: "3", sm: "5" }}
      pr={{ base: "4", sm: "6" }}
      py="2"
      borderLeftWidth="4px"
      borderColor="transparent"
      // active: borderColor='brand.500'
      // not for the first one: mt='1'
      fontSize="md"
      fontWeight="medium"
      color="gray.600"
      // active: color='brand.700'
      bg="white"
      // active: bg='brand.50'
      _focus={{
        outline: "none",
        color: "gray.800",
        bg: "gray.50",
        borderColor: "gray.300",
      }}
      // active:
      // _focus={{
      //   outline: 'none',
      //   color: 'brand.800',
      //   bg: 'brand.100',
      //   borderColor: 'brand.700',
      // }}
      _hover={{
        color: "gray.800",
        bg: "gray.50",
        borderColor: "gray.300",
      }}
      // active :
      // _hover={{ textDecoration: 'none' }}
      transition="all 150ms ease-in-out"
    >
      {name}
    </CustomLink>
  );
};

const NavBar = (props) => {
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const { user, loading, logout, isAuthenticated } = useAuth();
  const router = useRouter();
  const [isAdmin, setIsAdmin] = useState(false);
  const { handleError } = useContext(ContextStore);

  if (user) {
    fuego
      .auth()
      .currentUser?.getIdTokenResult()
      .then((idTokenResult) => {
        // Confirm the user is an Admin.
        if (!!idTokenResult.claims.administrator) {
          setIsAdmin(true); // Show admin UI.
        }
      })
      .catch((error) => {
        handleError({ error });
      });
  }

  return (
    <>
      <Box
        bg="white"
        w="full"
        // left='0'
        // top='0'
        // position='absolute'
        // position='fixed or sticky'
        // zIndex='sticky'
      >
        <Box maxW="7xl" mx="auto" px={{ base: "4", sm: "6", lg: "8" }}>
          <Flex justify={{ base: "start" }} align="center" h="16">
            {/* START SITE LOGO */}
            <Flex align="center" h="full" flexGrow="1" flexShrink="0" zIndex="10" pr="2">
              <CustomLink href="/">
                <Image h="10" w="auto" src="/images/logo.png" alt="La Citadelle" fallbackSrc="/images/logo.png" />
              </CustomLink>
            </Flex>
            {/* END SITE LOGO */}
            {/* START MOBILE RIGHT MENU */}
            <Box display={{ base: "block", md: "none" }}>
              {router.route === "/" && <Basket />}
              <Button aria-label="Menu" p="2" ml="2" onClick={() => setOpenMobileMenu(!openMobileMenu)}>
                <Box as={openMobileMenu ? XOutline : MenuOutline} size="20px" color="gray.600" />
              </Button>
            </Box>
            {/* END MOBILE RIGHT MENU */}
            {/* START DESKTOP RIGHT MENU */}
            <Box
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              justifyContent="flex-end"
              h="full"
              flexGrow="1"
              flexShrink="0"
              zIndex="10"
              pl="2"
            >
              <Stack isInline spacing={6} as="nav" h="full" mx="2" px="2" pr="4">
                <NavLink name="Menu" href="/" />
                <NavLink name="Accès" href="https://lacitadelle-stfons.fr/#acces" />
                <NavLink name="Contact" href="https://lacitadelle-stfons.fr/contact" />
                {/* {user && <NavLink name="Commandes" href="/compte/commandes" />} */}
              </Stack>

              {user ? (
                <Menu>
                  <MenuButton
                    as={Button}
                    variantColor="brand"
                    variant="outline"
                    rounded="lg"
                    mr={router.route === "/" ? "4" : "0"}
                  >
                    <Box as={UserCircleOutline} mr="1" size={18} />
                    Compte
                  </MenuButton>
                  <MenuList>
                    {isAdmin && (
                      <MenuItem h="10">
                        <CustomLink href="/admin" _hover={{ textDecoration: "none" }}>
                          Administration
                        </CustomLink>
                      </MenuItem>
                    )}
                    <MenuItem h="10">
                      <CustomLink href="/compte" _hover={{ textDecoration: "none" }}>
                        Mon compte
                      </CustomLink>
                    </MenuItem>
                    <MenuItem h="10">
                      <CustomLink href="/compte/commandes" _hover={{ textDecoration: "none" }}>
                        Mes commandes
                      </CustomLink>
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : (
                <NextLink href="/connexion" passHref>
                  <Button
                    as="a"
                    variantColor="brand"
                    variant="outline"
                    rounded="lg"
                    mr={router.route === "/" ? "4" : "0"}
                  >
                    <Box as={UserCircleOutline} mr="1" size={18} />
                    Connexion
                  </Button>
                </NextLink>
              )}

              {router.route === "/" && <Basket />}
            </Box>
            {/* END DESKTOP RIGHT MENU */}
          </Flex>
        </Box>
        {/* START MOBILE MENU */}
        <Box display={{ base: openMobileMenu ? "block" : "none", md: "none" }}>
          <Stack spacing={1} shouldWrapChildren pt="2" pb="3">
            <MobileNavLink name="Menu" href="/" />
            <MobileNavLink name="Accès" href="https://lacitadelle-stfons.fr/#acces" />
            <MobileNavLink name="Contact" href="https://lacitadelle-stfons.fr/contact" />
          </Stack>
          <Stack spacing={1} shouldWrapChildren pt="4" pb="3" borderTopWidth="1px" borderColor="gray.200">
            {user ? (
              <>
                <MobileNavLink name="Mon compte" href="/compte" />
                <MobileNavLink name="Mes commandes" href="/compte/commandes" />
              </>
            ) : (
              <MobileNavLink name="Connexion" href="/connexion" />
            )}
          </Stack>
        </Box>
        {/* END MOBILE MENU */}
      </Box>
    </>
  );
};

export default NavBar;
